import { useEffect, useMemo, useState } from "react";
import {
  Calendar,
  Package,
  Store,
  User,
  Clock,
  CreditCard,
  LoaderCircle,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Order, ProductVariant, SellerInfo, UserInfo } from "@/types/AppTypes";
import { fetchAllWithPagination } from "@/utils/helper/graphql-helper";
import { listSellerInfos, listUserInfos } from "@/graphql/queries";
import parse from "html-react-parser";
import { formatDate } from "@/ui-components/utils";
import { getStatusColor } from "../orderHistoryCard/OrderHistoryCard";

type OrderDetailsDialogProps = {
  order: Order;
  sellerName?: string;
  storeName?: string;
};

const OrderDetailsDialog = ({
  order,
  sellerName = "John's Electronics",
  storeName = "TechHub Store",
}: OrderDetailsDialogProps) => {
  const [open, setOpen] = useState(false);
  const [sellerInfo, setSellerInfo] = useState<UserInfo | null>(null);
  const [sellerBusinessInfo, setSellerBusinessInfo] = useState<SellerInfo | null>(null);
  const [loadingInfos, setLoadingInfos] = useState(false);
  const [variantData, setVariantData] = useState<ProductVariant>();
  const [shipping] = useState(5.99);

  useEffect(() => {
    const fetchSellerInfo = async () => {
      try {
        setLoadingInfos(true);

        const sellerInfoResult = await fetchAllWithPagination({
          query: listUserInfos,
          variables: {
            filter: {
              user_id: { eq: order?.seller_id },
            },
          },
        });
        if (sellerInfoResult?.[0]) {
          const sellerBusinessInfoResult = await fetchAllWithPagination({
            query: listSellerInfos,
            variables: {
              filter: {
                user_id: { eq: order?.seller_id },
              },
            },
          });
          if (sellerBusinessInfoResult?.[0]) {
            setSellerInfo(sellerInfoResult?.[0]);
            setSellerBusinessInfo(sellerBusinessInfoResult?.[0]);
          }
          const variantData =
            typeof order.variant === "string" ? JSON.parse(order.variant) : order.variant;
          setVariantData(variantData);
        }
      } catch (error) {
        console.log("Error Fetching SellerInfo", error);
      } finally {
        setLoadingInfos(false);
      }
    };
    fetchSellerInfo();
  }, []);

  const subtotal = useMemo(() => {
    if (variantData) {
      return ((variantData?.price ?? 0) * (order?.quantity ?? 1)).toFixed(2) || 0;
    }
    return 0;
  }, [order, variantData]);
  const tax = useMemo(() => (Number(subtotal) * 0.08).toFixed(2), [subtotal]);
  const total = useMemo(
    () => (Number(subtotal) + shipping + Number(tax)).toFixed(2),
    [subtotal, tax]
  );



  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          View Details
        </Button>
      </DialogTrigger>

      <DialogContent className=" data-[state=open]:max-w-[60rem] max-h-[90vh] overflow-y-auto">
        {loadingInfos ? (
          <div className="flex items-center justify-center data-[state=open]:max-w-[60rem] max-h-[90vh]l">
            <LoaderCircle className="w-6 h-6 animate-spin mr-4 "></LoaderCircle>
            Fetching order details. This won’t take long!
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-xl">
                <Package className="h-5 w-5" />
                Order Details - ORD-{order?.id?.slice(0, 4)}
              </DialogTitle>
            </DialogHeader>
            <DialogDescription>

            </DialogDescription>

            <div className="space-y-6">
              {/* Order Status and Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Order Information
                    </span>
                    <Badge className={getStatusColor(order.status)}>
                      {order.status?.toUpperCase() || "UNKNOWN"}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">Order Placed</p>
                        <p className="text-sm text-muted-foreground">
                          {formatDate(order.created_at)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">Quantity</p>
                        <p className="text-sm text-muted-foreground">
                          {order.quantity || 1} item(s)
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <CreditCard className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">Total Amount</p>
                        <p className="text-sm font-semibold">${total}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Product Details */}
              {variantData && (
                <Card>
                  <CardHeader>
                    <CardTitle>Product Details</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-col md:flex-row gap-6">
                      {/* Product Image */}
                      <div className="flex-shrink-0">
                        <div className="w-32 h-32 bg-gray-100 rounded-lg overflow-hidden">
                          {variantData.images && variantData.images.length > 0 ? (
                            <img
                              src={variantData.images?.[0] || "/placeholder.svg"}
                              alt={variantData.name || "Product"}
                              width={128}
                              height={128}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Package className="h-8 w-8 text-gray-400" />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Product Info */}
                      <div className="flex-1 space-y-4">
                        <div>
                          <h3 className="text-lg font-semibold">
                            {variantData.name || "Product Name"}
                          </h3>
                          <div className="text-sm text-muted-foreground mt-1">
                          
                              {parse(variantData?.description?.slice(0.40) ?? "") || "No description available"}
                            
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm font-medium">SKU</p>
                            <p className="text-sm text-muted-foreground">
                              {variantData.sku || "N/A"}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm font-medium">Price</p>
                            <p className="text-sm font-semibold">
                              ${variantData.price?.toFixed(2) || "0.00"}
                            </p>
                          </div>
                        </div>

                        {/* Product Attributes */}
                        {variantData.attributes &&
                          Object.keys(variantData.attributes).length > 0 && (
                            <div>
                              <p className="text-sm font-medium mb-2">Product Attributes</p>
                              <div className="flex flex-wrap gap-2">
                                {Object.entries(variantData.attributes).map(([key, value]) => (
                                  <Badge key={key} variant="secondary" className="text-xs">
                                    {key}: {String(value)}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Seller Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Store className="h-4 w-4" />
                    Seller Information
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                        <User className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">Seller Name</p>
                        <p className="text-sm text-muted-foreground">
                          {sellerInfo?.first_name + " " + sellerInfo?.last_name}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                        <Store className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">Store Name</p>
                        <p className="text-sm text-muted-foreground">
                          {sellerBusinessInfo?.business_name}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Order Summary */}
              <Card>
                <CardHeader>
                  <CardTitle>Order Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Subtotal: ${subtotal}</span>
                      <span className="text-sm">${subtotal}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Shipping</span>
                      <span className="text-sm">{shipping}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Tax</span>
                      <span className="text-sm">${tax}</span>
                    </div>
                    <Separator />
                    <div className="flex justify-between items-center font-semibold">
                      <span>Total</span>
                      <span>${total}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Additional Order Info */}
              {/* <Card>
              <CardHeader>
                <CardTitle>Additional Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="font-medium">Order ID</p>
                    <p className="text-muted-foreground">{order.id}</p>
                  </div>
                  <div>
                    <p className="font-medium">Last Updated</p>
                    <p className="text-muted-foreground">{formatDate(order.updated_at)}</p>
                  </div>
                  {order.variant?.product_id && (
                    <div>
                      <p className="font-medium">Product ID</p>
                      <p className="text-muted-foreground">{order.variant.product_id}</p>
                    </div>
                  )}
                  {order.seller_id && (
                    <div>
                      <p className="font-medium">Seller ID</p>
                      <p className="text-muted-foreground">{order.seller_id}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card> */}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OrderDetailsDialog;
